import { z } from 'zod'
import { makeFieldKeys } from '../../core/utils/utilities.ts'

/**
 * Agent Access 4.5 on-record provenance, as the detail RPCs project it.
 *
 * Shape of the `prog__created_provenance` object emitted by
 * `public.agent_created_provenance()` and carried by every record model whose
 * full form shows the marker (deal, account, contact, prospect). Null on the
 * wire is the ordinary case: a record a human created — the form renders
 * nothing.
 *
 * Keys are the resolver's own, not a table's, so they carry no `table__`
 * alias. `created_actor` is the raw signed claim (`agent:<key>`), `agent_key`
 * that key alone, `agent_name` its cfg.agents display name (falling back to
 * the key for a retired product), `created_by_name` the user the agent acted
 * for, and `date_created` when.
 */
export const AgentProvenanceEntrySchema = z
  .object({
    /**
     * Which provenance story this is.
     * 'live'     - the write funnel stamped `created_actor` from the signed
     *              session claim; the agent both drafted and committed.
     * 'approved' - the agent drafted it and a human approved the replay, so
     *              `created_actor` is null and the agent identity came from
     *              the agent_proposals journal. `created_by_name` is the
     *              approving human.
     * Typed as a plain string, deliberately NOT a z.enum: a third mode added
     * in SQL later must not fail validation and break the whole record.
     */
    mode: z.string().nullish(),
    created_actor: z.string().nullish(),
    agent_key: z.string().nullish(),
    agent_name: z.string().nullish(),
    created_by_name: z.string().nullish(),
    date_created: z.string().nullish()
  })
  .passthrough()

export const AgentProvenanceEntryFields = makeFieldKeys(AgentProvenanceEntrySchema)

export type AgentProvenanceEntry = z.infer<typeof AgentProvenanceEntrySchema>
